import React, { useMemo, useState } from 'react';
import {
  Building2, ChevronDown, ChevronRight, Mail, Route, ShieldCheck, Trash2, UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';

const TERMINAL_STATUSES = new Set(['accepted', 'declined', 'closed']);

const statusLabel = status => ({
  new: 'Received',
  contacted: 'Under review',
  approved: 'Approved',
  organization_created: 'Organisation created',
  invited: 'Invitation sent',
  accepted: 'Joined Qflow',
  declined: 'Declined',
  closed: 'Closed',
}[status] || String(status || 'Received').replaceAll('_', ' '));

const statusTone = status => ({
  new: 'bg-sky-100 text-sky-700',
  contacted: 'bg-amber-100 text-amber-800',
  approved: 'bg-indigo-100 text-indigo-700',
  organization_created: 'bg-violet-100 text-violet-700',
  invited: 'bg-blue-100 text-blue-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-red-100 text-red-700',
  closed: 'bg-slate-100 text-slate-600',
}[status] || 'bg-slate-100 text-slate-600');

const formatDate = value => value ? new Date(value).toLocaleString() : 'Date unavailable';

export default function SignupRequestsPanel({
  requests = [],
  organizations = [],
  onAddOrganization,
  onUpdateStatus,
  onRouted,
  onDeleteRequest,
}) {
  const [showAll, setShowAll] = useState(false);
  const [expandedId, setExpandedId] = useState('');
  const [routeDraft, setRouteDraft] = useState({ requestId: '', tenantId: '' });
  const [routing, setRouting] = useState(false);

  const ordered = useMemo(() => [...requests].sort((left, right) => {
    const leftDone = TERMINAL_STATUSES.has(left.status);
    const rightDone = TERMINAL_STATUSES.has(right.status);
    if (leftDone !== rightDone) return leftDone ? 1 : -1;
    return new Date(right.created_at || 0) - new Date(left.created_at || 0);
  }), [requests]);
  const pendingCount = ordered.filter(item => !TERMINAL_STATUSES.has(item.status)).length;
  const visible = showAll ? ordered : ordered.slice(0, 5);
  const activeOrganizations = organizations.filter(item => item.status === 'active');

  const beginRouting = request => {
    setExpandedId(request.id);
    setRouteDraft({ requestId: request.id, tenantId: request.matched_tenant_id || '' });
  };

  const routeRequest = async request => {
    if (!routeDraft.tenantId) return toast.error('Choose an existing organisation');
    setRouting(true);
    try {
      const { data } = await axiosInstance.post(
        `/platform/signup-requests/${encodeURIComponent(request.id)}/route`,
        { tenant_id: routeDraft.tenantId },
      );
      toast.success(`Request sent privately to ${data.matched_organization_name}'s Tenant Admin`);
      setRouteDraft({ requestId: '', tenantId: '' });
      await onRouted?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not route this signup request');
    } finally { setRouting(false); }
  };

  return <section className="overflow-hidden rounded-xl border border-sky-200 bg-white" data-testid="signup-request-queue">
    <header className="flex flex-wrap items-start justify-between gap-3 border-b border-sky-100 bg-sky-50/70 p-4">
      <div className="flex items-start gap-3">
        <ShieldCheck size={19} className="mt-0.5 text-sky-700"/>
        <div>
          <h2 className="font-semibold text-sky-950">Qflow signup requests</h2>
          <p className="mt-1 text-sm text-sky-800">Review new organisations and securely route people who belong to an existing tenant.</p>
        </div>
      </div>
      <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-sky-800 shadow-sm">{pendingCount} pending</span>
    </header>

    {!ordered.length ? <div className="px-5 py-8 text-center text-sm text-slate-500">No signup requests are waiting for review.</div> : <div className="divide-y">
      {visible.map(request => {
        const expanded = expandedId === request.id;
        const terminal = TERMINAL_STATUSES.has(request.status);
        const linkedOrganization = organizations.find(item => item.tenant_id === request.matched_tenant_id);
        const canAddOrganization = !terminal && !request.matched_tenant_id && !['organization_created', 'invited'].includes(request.status);
        return <article key={request.id} className="bg-white">
          <div className="flex items-center gap-2 px-4 py-3 hover:bg-slate-50">
            <button type="button" onClick={() => setExpandedId(expanded ? '' : request.id)} aria-expanded={expanded} className="flex min-w-0 flex-1 items-start gap-3 text-left">
              {expanded ? <ChevronDown size={16} className="mt-1 shrink-0 text-slate-500"/> : <ChevronRight size={16} className="mt-1 shrink-0 text-slate-500"/>}
              <Building2 size={17} className="mt-0.5 shrink-0 text-indigo-600"/>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2"><strong className="truncate text-sm text-slate-900">{request.organization_name}</strong><span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${statusTone(request.status)}`}>{statusLabel(request.status)}</span>{linkedOrganization && <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] text-violet-700">Linked to {linkedOrganization.name}</span>}</div>
                <p className="mt-1 truncate text-xs text-slate-500">{request.name} · {request.work_email}</p>
              </div>
              <time className="hidden shrink-0 text-xs text-slate-400 lg:block">{formatDate(request.created_at)}</time>
            </button>
            {canAddOrganization && <button type="button" onClick={() => onAddOrganization(request)} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-medium text-white"><UserPlus size={14}/>Add organization</button>}
          </div>

          {expanded && <div className="border-t bg-slate-50/50 px-5 py-4">
            <div className="grid gap-3 text-sm md:grid-cols-3">
              <div><span className="block text-xs font-medium text-slate-500">Requested by</span><span className="mt-1 flex items-center gap-1 text-slate-800"><Mail size={13}/>{request.name} · {request.work_email}</span></div>
              <div><span className="block text-xs font-medium text-slate-500">Job title</span><span className="mt-1 block text-slate-800">{request.job_title || request.role || 'Not supplied'}</span></div>
              {request.team_size && <div><span className="block text-xs font-medium text-slate-500">Organisation size</span><span className="mt-1 block text-slate-800">{request.team_size}</span></div>}
            </div>
            {request.use_case && <p className="mt-3 rounded-lg bg-white p-3 text-sm leading-6 text-slate-700">{request.use_case}</p>}
            {request.review_note && <p className="mt-2 text-xs text-slate-500">Review note: {request.review_note}</p>}
            {request.tenant_review_status && <p className="mt-2 text-xs text-violet-700">Tenant review: <strong>{statusLabel(request.tenant_review_status)}</strong></p>}

            {routeDraft.requestId === request.id && !terminal && <div className="mt-4 grid gap-2 rounded-lg border border-violet-200 bg-violet-50 p-3 md:grid-cols-[1fr_auto]">
              <label className="text-xs font-medium text-violet-950">Existing organisation<select value={routeDraft.tenantId} disabled={Boolean(request.matched_tenant_id)} onChange={event => setRouteDraft(current => ({ ...current, tenantId: event.target.value }))} className="mt-1 block h-10 w-full rounded-lg border bg-white px-3 text-sm disabled:bg-slate-100"><option value="">Choose an organisation</option>{activeOrganizations.map(item => <option key={item.tenant_id} value={item.tenant_id}>{item.name}</option>)}</select></label>
              <button type="button" disabled={routing || !routeDraft.tenantId || request.tenant_review_status === 'accepted'} onClick={() => routeRequest(request)} className="h-10 self-end rounded-lg bg-violet-700 px-4 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"><Route size={15} className="mr-1.5 inline"/>{routing ? 'Routing…' : request.matched_tenant_id ? 'Notify Tenant Admin again' : 'Route to Tenant Admin'}</button>
            </div>}

            <div className="mt-4 flex flex-wrap gap-2">
              {!terminal && <button type="button" onClick={() => beginRouting(request)} className="inline-flex items-center gap-1.5 rounded-lg border border-violet-200 bg-white px-3 py-2 text-xs font-medium text-violet-700"><Route size={14}/>{request.matched_tenant_id ? 'Tenant routing details' : 'Route to existing organization'}</button>}
              {!terminal && request.status === 'new' && <button type="button" onClick={() => onUpdateStatus(request, 'contacted')} className="rounded-lg border border-sky-200 bg-white px-3 py-2 text-xs text-sky-700">Mark under review</button>}
              {!terminal && <button type="button" onClick={() => onUpdateStatus(request, 'declined')} className="rounded-lg border border-red-200 bg-white px-3 py-2 text-xs text-red-700">Decline</button>}
              {!terminal && <button type="button" onClick={() => onUpdateStatus(request, 'closed')} className="rounded-lg border bg-white px-3 py-2 text-xs text-slate-600">Close</button>}
              <button type="button" onClick={() => onDeleteRequest(request)} className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-medium text-red-700"><Trash2 size={14}/>Delete request details</button>
            </div>
          </div>}
        </article>;
      })}
    </div>}
    {ordered.length > 5 && <div className="border-t bg-slate-50 px-4 py-3 text-center"><button type="button" onClick={() => setShowAll(current => !current)} className="text-sm font-medium text-indigo-700">{showAll ? 'Show fewer requests' : `View all ${ordered.length} requests`}</button></div>}
  </section>;
}
